import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type { Payment, PaymentFilters, CreatePaymentInput } from '@erp/data-access/interfaces';

const PAYMENTS_KEY = ['payments'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

/**
 * Always fetches the full payments list and filters in memory so
 * client/supplier/document profile pages, dashboard widgets, and the
 * caisse all hit the prewarmed cache offline.
 */
export function usePayments(filters?: PaymentFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...PAYMENTS_KEY, undefined],
    queryFn: () => ds.payments.list(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { paymentType, thirdPartyType, thirdPartyId, dateFrom, dateTo, documentId, documentType, recurringPaymentId } = filters;
    return query.data.filter((p) => {
      if (paymentType && p.paymentType !== paymentType) return false;
      if (thirdPartyType && p.thirdPartyType !== thirdPartyType) return false;
      if (thirdPartyId && p.thirdPartyId !== thirdPartyId) return false;
      if (dateFrom && p.paymentDate < dateFrom) return false;
      if (dateTo && p.paymentDate > dateTo) return false;
      if (documentId || documentType) {
        // Document filter requires a matching allocation row.
        const allocs = p.allocations ?? [];
        if (!allocs.some((a) =>
          (!documentId || a.documentId === documentId) &&
          (!documentType || a.documentType === documentType),
        )) return false;
      }
      if (recurringPaymentId && p.recurringPaymentId !== recurringPaymentId) return false;
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useDocumentPayments(documentId: string | undefined, documentType: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...PAYMENTS_KEY, 'doc', documentType, documentId],
    queryFn: () => ds.payments.list({ documentId: documentId!, documentType }),
    enabled: !!documentId,
  });
}

export function usePayment(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...PAYMENTS_KEY, id],
    queryFn: () => ds.payments.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<Payment>(qc, PAYMENTS_KEY, id),
  });
}

export function useCreatePayment() {
  const ds = useDataService();
  return useResilientMutation<CreatePaymentInput, Payment>({
    entity: 'payments',
    op: 'create',
    invalidate: [PAYMENTS_KEY],
    online: (input) => ds.payments.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        number: OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
        paymentType: input.paymentType,
        paymentMethod: input.paymentMethod,
        thirdPartyType: input.thirdPartyType,
        thirdPartyId: input.thirdPartyId,
        bankAccountId: input.bankAccountId ?? null,
        cashSessionId: null,
        amount: input.amount,
        paymentDate: input.paymentDate ?? new Date().toISOString(),
        reference: input.reference ?? null,
        notes: input.notes ?? null,
        recurringPaymentId: input.recurringPaymentId ?? null,
        createdAt: new Date().toISOString(),
      }) as Payment,
  });
}

export function useDeletePayment() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'payments',
    op: 'delete',
    invalidate: [PAYMENTS_KEY],
    online: (id) => ds.payments.delete(id),
    optimistic: () => undefined as unknown as void,
  });
}
